import React from "react";
import { View, Text } from "@react-pdf/renderer";
import PDFTableTitleRow, { PDFTableTitleRowVariant } from "./TableTitleRow";
import PDFTableRow from "./TableRow";
import colors from "core/resources/theme/colors";
import { formatMoney } from "infra/helpers/number";
import PDFEmptyTable from "./EmptyTable";

export interface PDFTableColumn<T> {
  title: string;
  render?: (item: T, index: number) => JSX.Element | null;
  accessor?: keyof T;
  width?: number | string;
  flex?: number;
  flexGrow?: number;
  flexShrink?: number;
  color?: string;
  fontStyle?: string;
  textAlign?: "left" | "center" | "right";
  fontSize?: number;
  paddingHorizontal?: number;
  paddingRight?: number;
  shouldHide?: boolean;
}

interface PDFTableProps<T> {
  columns: PDFTableColumn<T>[];
  data: T[];
  totalRow?: number;
  tableHeaderVariant?: PDFTableTitleRowVariant;
  tags?: string[];
  wrap?: boolean;
  extraRows?: {
    index: number;
    component: React.ReactNode;
  }[];
}

function PDFTable<T>({
  columns,
  data,
  totalRow,
  tableHeaderVariant = "primary",
  tags,
  extraRows,
  wrap,
}: PDFTableProps<T>) {
  if (!data.length) {
    return (
      <View>
        <PDFEmptyTable />
      </View>
    );
  }

  return (
    <View wrap={wrap}>
      {tableHeaderVariant !== "invisible" ? (
        <View>
          <PDFTableTitleRow
            columns={columns}
            variant={tableHeaderVariant}
            tags={tags}
          />
        </View>
      ) : null}

      <View>
        {data.map((item, index) => (
          <React.Fragment key={index}>
            <PDFTableRow
              key={index}
              backgroundColor={index % 2 === 0 ? colors.white : colors.black100}
              item={item}
              columns={columns}
            />
            {index === (data?.length || 0) - 1 && Number.isFinite(totalRow) ? (
              <PDFTableRow
                key={index + 100}
                backgroundColor={
                  (index + 1) % 2 === 0 ? colors.white : colors.black100
                }
                item={{
                  title: "Total",
                  value: formatMoney(totalRow),
                }}
                columns={[
                  {
                    flexGrow: 4,
                    title: "Total",
                    render: (item) => (
                      <Text style={{ fontWeight: "bold" }}>{item.title}</Text>
                    ),
                  },
                  {
                    flexGrow: 4,
                    title: "Total",
                    render: (item) => (
                      <Text
                        style={{
                          fontWeight: "bold",
                          marginLeft: "auto",
                          textAlign: "left",
                        }}
                      >
                        {item.value}
                      </Text>
                    ),
                  },
                ]}
              />
            ) : (
              <></>
            )}
            {extraRows
              ?.filter((row) => row.index === index)
              ?.map((row, _index) => (
                <React.Fragment key={index}>{row.component}</React.Fragment>
              ))}
          </React.Fragment>
        ))}
      </View>
    </View>
  );
}

export default PDFTable;
